import { createStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { get } from '@enterprise/core';

import { datastreamApi } from '@/common/datastream-api';
import { enterpriseApi } from '@/common/enterprise-api';

import type { ApplicationStore } from './types/application-store.type';

/**
 * The application's global Zustand store.
 *
 * Module-level singleton — created once when this module is first imported.
 * Read it through {@link useApplicationStore} in components and hooks; direct
 * `applicationStore.getState()` / `.setState()` access is available for
 * non-React contexts.
 */
export const applicationStore = createStore<ApplicationStore>(
  // @ts-expect-error - Zustand's `createStore` typing does not handle middleware well
  persist(
    (set, zustandGet) => {
      return {
        datastreamApi,
        enterpriseApi,
        getTooltip: (name: string) => {
          const tooltips = zustandGet().tooltips;

          return get(tooltips, name);
        },
        gridColumnState: {},
        setDatastreamApi: (datastreamApi) => {
          set({ datastreamApi });
        },
        setEnterpriseApi: (enterpriseApi) => {
          set({ enterpriseApi });
        },
        setGridColumnState: (key, columnState) => {
          set((state) => {
            return {
              gridColumnState: {
                ...state.gridColumnState,
                [key]: columnState,
              },
            };
          });
        },
        setLastValidatedAt: (lastValidatedAt) => {
          set({ lastValidatedAt });
        },
        setTooltips: (tooltips) => {
          set({ tooltips });
        },
        setUser: (user) => {
          set({ user });
        },
        tooltips: {},
        user: undefined,
      };
    },
    {
      name: 'datastream-application-store',
      onRehydrateStorage: () => {
        return (_) => {
          // add custom logic here if needed
        };
      },
      storage: createJSONStorage(() => {
        return localStorage;
      }),
    }
  )
);
